import { Field, FieldProps, Form, Formik } from 'formik'
import _ from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { usePlacesWidget } from 'react-google-autocomplete'
import { useDispatch } from 'react-redux'
import { useAthlete } from 'src/common/hooks/useAthlete'
import { getSalesTax, useGetStatesDataQuery } from 'src/services/mainAPI'
import { updateShippingInfo } from 'src/store/joinevent'
import * as Yup from 'yup'
import StateListBox from './StateListBox'

const ShippingAddrSchema = Yup.object().shape({
    shippingAddress: Yup.string().required('Please enter your address'),
    shippingAddress2: Yup.string(),
    city: Yup.string().required('Please enter your city'),
    stateId: Yup.number().required(
        'Please select your state from the dropdown (select "International" for addresses outside the US'
    ),
    zipCode: Yup.string().required('Please enter a zip code'),
    placeId: Yup.string()
})

export const ShippingSection = ({ formRef }) => {
    const athlete = useAthlete()
    const dispatch = useDispatch()
    const { data: statesData } = useGetStatesDataQuery(null)

    const [selectedPlace, setSelectedPlace] = useState(null)
    const { ref: autocompleteRef } = usePlacesWidget({
        apiKey: 'AIzaSyB3k__HMXpt2VkvcjjjZnLG75Adaq5j16I',
        onPlaceSelected: (place) => {
            setSelectedPlace(place)
        },
        inputAutocompleteValue: '',
        language: 'en',
        options: {
            types: ['address']
        }
    })
    const [mappedStatesData, setMappedStatesData] = useState([])

    const setFieldsFromGAQuery = useCallback(
        (place) => {
            const address = _.get(place, 'address_components')
            const placeId = _.get(place, 'place_id')
            let streetNumber = ''
            let route = ''
            let city = null
            let state = null
            let zip = null
            let isUS = null
            let stateId = null

            _.forEach(address, function (component) {
                const types = _.get(component, 'types')

                if (_.includes(types, 'street_number')) {
                    streetNumber = _.get(component, 'long_name')
                }

                if (_.includes(types, 'route')) {
                    route = _.get(component, 'long_name')
                }

                if (_.includes(types, 'locality') || _.includes(types, 'sublocality')) {
                    city = _.get(component, 'long_name')
                }

                if (_.includes(types, 'administrative_area_level_1')) {
                    state = _.get(component, 'short_name')
                }

                if (_.includes(types, 'postal_code')) {
                    zip = _.get(component, 'long_name')
                }

                if (_.includes(types, 'country')) {
                    isUS = _.get(component, 'short_name') === 'US' ? true : false
                }
            })

            const streetAddress = `${streetNumber} ${route}`

            if (isUS) {
                _.forEach(statesData, function (s) {
                    if (_.includes(s, state)) {
                        stateId = _.get(s, 'stateId')
                    }
                })
            } else {
                stateId = statesData?.find((i) => i.abbrev === 'IT')?.stateId ?? 0
            }

            formRef.current.setFieldValue('shippingAddress', streetAddress.trim())
            formRef.current.setFieldValue('shippingAddress2', '')
            formRef.current.setFieldValue('city', city ?? '')
            formRef.current.setFieldValue('stateId', stateId ?? '')
            formRef.current.setFieldValue('zipCode', zip ?? '')
            formRef.current.setFieldValue('placeId', placeId ?? '')
            void formRef.current.validateForm()
        },
        [statesData, formRef]
    )

    useEffect(() => {
        if (statesData?.length) {
            const newStatesData = statesData.map((state) => {
                return {
                    value: state.stateId,
                    label: state.description
                }
            })
            setMappedStatesData(newStatesData)
        }
    }, [statesData])

    useEffect(() => {
        if (selectedPlace) {
            setFieldsFromGAQuery(selectedPlace)
        }
    }, [selectedPlace])

    useEffect(() => {
        function setInitialFieldValues(a) {
            formRef.current.setFieldValue(
                'shippingAddress',
                a.user.shippingAddress?.split(',')[0]?.trim() ?? '',
                true
            )
            formRef.current.setFieldValue(
                'shippingAddress2',
                a.user.shippingAddress?.split(',')[1]?.trim() ?? '',
                true
            )
            formRef.current.setFieldValue('city', a.user.city, true)
            formRef.current.setFieldValue('stateId', a.user.stateId, true)
            formRef.current.setFieldValue('zipCode', a.user.zipCode, true)
            formRef.current.setFieldValue('placeId', a.user.placeId ?? '', true)
        }

        if (
            athlete.user?.shippingAddress &&
            athlete.user?.city &&
            athlete.user?.stateId &&
            athlete.user?.zipCode
        ) {
            try {
                setInitialFieldValues(athlete)
            } catch (e) {
                // eslint-disable-next-line no-console
                console.log(e)
            }
        }
    }, [
        athlete.user?.city,
        athlete.user?.placeId,
        athlete.user?.shippingAddress,
        athlete.user?.stateId,
        athlete.user?.zipCode
    ])

    return (
        <div className="w-full shadow-md rounded-md p-8 border border-gray-200 bg-white my-6">
            <div className="flex items-center">
                <div className="w-10 h-10 border-2 border-yf-green rounded-full flex items-center justify-center font-medium mr-5 text-lg flex-shrink-0">
                    2
                </div>
                <p className="uppercase text-yf-green font-medium text-xl md:text-2xl">
                    Shipping Information
                </p>
            </div>
            <div className="h-px bg-gray-300 my-4"></div>
            <Formik
                innerRef={formRef}
                validateOnChange={true}
                validateOnMount={false}
                initialValues={{
                    shippingAddress: '',
                    shippingAddress2: '',
                    city: '',
                    stateId: '',
                    zipCode: '',
                    placeId: ''
                }}
                validationSchema={ShippingAddrSchema}
                onSubmit={(values) => {
                    const combinedShippingAddress = `${values.shippingAddress}${
                        values.shippingAddress2?.length
                            ? `, ${values.shippingAddress2}`
                            : ''
                    }`
                    const realValues = {
                        ...values,
                        shippingAddress: combinedShippingAddress
                    }
                    dispatch(updateShippingInfo(realValues))
                    dispatch(
                        getSalesTax.initiate({
                            line1: realValues.shippingAddress,
                            city: realValues.city,
                            zip: realValues.zipCode,
                            stateId: realValues.stateId
                        })
                    )
                }}
            >
                {({ isSubmitting }) => (
                    <Form>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field name="shippingAddress">
                                {({
                                    field, // { name, value, onChange, onBlur }
                                    meta
                                }) => (
                                    <div>
                                        <label>Street Address</label>
                                        <input
                                            disabled={isSubmitting}
                                            type="text"
                                            placeholder=""
                                            className="mt-1 block w-full rounded-md py-3 border-gray-300 focus:border-yf-green focus:ring-1 focus:ring-yf-green placeholder-gray-400 disabled:bg-gray-300 disabled:opacity-50"
                                            {...field}
                                            ref={autocompleteRef}
                                        />
                                        {meta.touched && meta.error && (
                                            <span className="text-red-500 text-sm">
                                                {meta.error}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </Field>

                            <Field name="shippingAddress2">
                                {({
                                    field, // { name, value, onChange, onBlur }
                                    meta
                                }) => (
                                    <div>
                                        <label>Apt/Suite #</label>
                                        <input
                                            disabled={isSubmitting}
                                            type="text"
                                            className="mt-1 block w-full rounded-md py-3 border-gray-300 focus:border-yf-green focus:ring-1 focus:ring-yf-green placeholder-gray-400 disabled:bg-gray-300 disabled:opacity-50"
                                            {...field}
                                        />
                                        {meta.touched && meta.error && (
                                            <span className="text-red-500 text-sm">
                                                {meta.error}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </Field>

                            <Field name="city">
                                {({
                                    field, // { name, value, onChange, onBlur }
                                    meta
                                }) => (
                                    <div>
                                        <label>City</label>
                                        <input
                                            disabled={isSubmitting}
                                            type="text"
                                            className="mt-1 block w-full rounded-md py-3 border-gray-300 focus:border-yf-green focus:ring-1 focus:ring-yf-green placeholder-gray-400 disabled:bg-gray-300 disabled:opacity-50"
                                            {...field}
                                        />
                                        {meta.touched && meta.error && (
                                            <span className="text-red-500 text-sm">
                                                {meta.error}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </Field>

                            <Field name="stateId">
                                {({
                                    field, // { name, value, onChange, onBlur }
                                    form, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
                                    meta
                                }: FieldProps) => (
                                    <div>
                                        <label>State</label>
                                        <StateListBox
                                            disabled={isSubmitting}
                                            value={field.value}
                                            states={mappedStatesData}
                                            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                                            onChange={(state) => {
                                                form.setFieldValue('stateId', state)
                                            }}
                                        />
                                        {meta.touched && meta.error && (
                                            <span className="text-red-500 text-sm">
                                                {meta.error}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </Field>

                            <Field name="zipCode">
                                {({
                                    field, // { name, value, onChange, onBlur }
                                    meta
                                }: FieldProps) => (
                                    <div>
                                        <label>Zip Code</label>
                                        <input
                                            disabled={isSubmitting}
                                            type="text"
                                            className="mt-1 block w-full rounded-md py-3 border-gray-300 focus:border-yf-green focus:ring-1 focus:ring-yf-green placeholder-gray-400 disabled:bg-gray-300 disabled:opacity-50"
                                            {...field}
                                        />
                                        {meta.touched && meta.error && (
                                            <span className="text-red-500 text-sm">
                                                {meta.error}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </Field>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    )
}
